import { useCallback, useEffect, useState } from 'react';
import type { Lead, LeadStatus, SiteContext } from '@icetus/contracts';
import {
  LEAD_KIND_LABELS,
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  formatLeadTime,
  leadsToCsv,
} from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import { fetchLeads, fetchSites, setLeadStatus } from '../api';
import { downloadCsv } from '../lib/download';
import type { LoadPhase } from '../lib/load-phase';

/**
 * 询盘收件箱。
 *
 * 这一页解决的问题不是"看数据"，而是"别漏掉一个买家"：
 * 外贸业务员一天要看几十封信，最怕的是"上周有人要目录，没人回"。
 * 所以默认排序按时间倒序、默认筛选是全部（不是未读），
 * 未读数只作为侧栏红点提醒 —— 因为筛到"只看未读"之后，
 * 读过的线索会从眼前消失，而它们通常比未读的更值得跟进。
 *
 * 不显示 IP 与 UA：它们在库里（用于举证与排查），但不该出现在日常界面里。
 * 业务员看这些没有用，而"客户知道我们记了他的 IP"只会让转化变差。
 */

const FILTERS: Array<{ key: LeadStatus | 'all'; label: string }> = [
  { key: 'all', label: '全部' },
  ...LEAD_STATUSES.map((status) => ({ key: status, label: LEAD_STATUS_LABELS[status] })),
];

const STATUS_TONE: Record<LeadStatus, 'neutral' | 'success' | 'warn' | 'danger'> = {
  new: 'warn',
  read: 'success',
  archived: 'neutral',
  spam: 'danger',
};

export function LeadsPage({ tenantId }: { tenantId: string }) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [siteId, setSiteId] = useState('');
  const [filter, setFilter] = useState<LeadStatus | 'all'>('all');
  const [items, setItems] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<LoadPhase>('loading');

  useEffect(() => {
    let cancelled = false;
    fetchSites(tenantId)
      .then((list) => {
        if (cancelled) return;
        setSites(list);
      })
      .catch(() => {
        // 站点列表只用于筛选下拉；拿不到就不显示那个下拉，不影响看询盘
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const data = await fetchLeads(tenantId, {
        ...(siteId ? { siteId } : {}),
        ...(filter === 'all' ? {} : { status: filter }),
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
      setUnread(data.unread);
      setPhase('ready');
    } catch (err) {
      setError((err as Error).message);
      setPhase('error');
    } finally {
      setBusy(false);
    }
  }, [tenantId, siteId, filter]);

  useEffect(() => {
    setPhase('loading');
    void reload();
  }, [reload]);

  const mark = async (lead: Lead, status: LeadStatus) => {
    setBusy(true);
    try {
      await setLeadStatus(tenantId, lead.id, status);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`询盘-${stamp}.csv`, leadsToCsv(items));
  };

  return (
    <Card>
      <div className="tablebar">
        <div className="platform-filters">
          {FILTERS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={filter === item.key ? 'pill pill--on' : 'pill'}
              onClick={() => setFilter(item.key)}
            >
              {item.label}
              {item.key === 'new' && unread > 0 ? ` ${unread}` : ''}
            </button>
          ))}
        </div>
        {sites.length > 1 ? (
          <div className="picker">
            <span>站点</span>
            <select className="field" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">全部站点</option>
              {sites.map((s) => (
                <option key={s.site.id} value={s.site.id}>
                  {s.site.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {/* 导出放在这一行末尾：卡片标题已经去掉，单独留一行只放一颗按钮
            等于用一整行的空白换一颗导出 */}
        <div className="tablebar__right">
          <Button variant="ghost" disabled={items.length === 0} onClick={exportCsv}>
            导出 CSV
          </Button>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}

      {phase === 'loading' ? (
        <p className="muted">正在读取…</p>
      ) : items.length === 0 ? (
        <div className="empty">
          <p className="empty__title">{filter === 'all' ? '还没有收到询盘' : '这个分类下暂时没有'}</p>
          <p className="muted">
            在页面上放一个「资料下载（留邮箱）」区块，买家留邮箱拿目录时就会在这里生成一条线索。
            联系表单收到的留言也进这一页。
          </p>
        </div>
      ) : (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>时间</th>
                <th>来源</th>
                <th>买家</th>
                <th>留言</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((lead) => (
                <tr key={lead.id}>
                  <td className="muted platform-nowrap">{formatLeadTime(lead.createdAt)}</td>
                  {/*
                    「来源」= 这条线索是从哪儿来的：哪种表单，加上资料名或页面路径。
                    页面路径原来挂在「留言」列下面，读起来像留言的第二段，
                    而且联系表单那几行的路径就是 /contact，与「联系表单」两个字
                    看着像重复 —— 但它其实回答的是"在哪一页提交的"，挪到这一列才对。
                  */}
                  <td className="platform-nowrap">
                    <div>{LEAD_KIND_LABELS[lead.kind]}</div>
                    <div className="muted platform-sub">
                      {lead.resourceId
                        ? (lead.resourceName ?? '（资料已删除）')
                        : lead.pagePath || '—'}
                    </div>
                  </td>
                  <td>
                    <div className="platform-tenant">
                      <a href={`mailto:${lead.email}`}>{lead.email}</a>
                    </div>
                    <div className="muted platform-sub">
                      {[lead.name, lead.company, lead.country].filter(Boolean).join(' · ') || '—'}
                      {lead.phone ? ` · ${lead.phone}` : ''}
                    </div>
                  </td>
                  <td>
                    <div className="lead-message" title={lead.message}>
                      {lead.message || '—'}
                    </div>
                  </td>
                  <td>
                    <Badge tone={STATUS_TONE[lead.status]}>{LEAD_STATUS_LABELS[lead.status]}</Badge>
                  </td>
                  {/*
                    三个动作对应三种后果，用三种颜色分开：读了（主色）、
                    收起来（灰）、当垃圾（红）。原来全是一挂灰按钮，
                    业务员每次都得先读字再点，而「垃圾」点错一次就找不到这条线索了。
                  */}
                  <td className="platform-nowrap">
                    <div className="row-actions">
                      {lead.status !== 'read' ? (
                        <Button size="sm" variant="primary" disabled={busy} onClick={() => void mark(lead, 'read')}>
                          标为已读
                        </Button>
                      ) : null}
                      {lead.status !== 'archived' ? (
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void mark(lead, 'archived')}>
                          归档
                        </Button>
                      ) : null}
                      {lead.status !== 'spam' ? (
                        <Button size="sm" variant="danger" disabled={busy} onClick={() => void mark(lead, 'spam')}>
                          垃圾
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted platform-meta">
        {/* total 跟着筛选走（服务端这么算的），所以这里不用再解释"合计见列表" */}
        共 {total} 条{filter === 'all' ? '' : '（当前筛选）'}，本页显示 {items.length} 条。
        {unread > 0 ? `另有 ${unread} 条未读。` : ''}
      </p>
    </Card>
  );
}
